import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Button, Box, Paper } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';

const useStyles = makeStyles((theme) => ({
  warningPaper: {
    padding: theme.spacing(2.5),
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1.25),
  },
  warningTitle: {
    color: '#856404',
    marginBottom: theme.spacing(1.25),
  },
  warningText: {
    color: '#856404',
    marginBottom: theme.spacing(2.5),
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(1.25),
    '& > *': {
      marginRight: theme.spacing(1.25),
    },
    '& > *:last-child': {
      marginRight: 0,
    },
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  triggerButton: {
    backgroundColor: '#f44336',
    color: '#fff',
  },
}));

/**
 * Reusable delete confirmation dialog component
 * 
 * @param {string} title - Dialog title (e.g., "Delete Entry")
 * @param {string} message - Confirmation message
 * @param {function} onConfirm - Callback when user confirms deletion
 * @param {function} onCancel - Callback when user cancels
 * @param {boolean} showWarning - Whether to show the warning dialog or button
 * @param {boolean} isLoading - Whether deletion is in progress
 * @param {string} confirmLabel - Label for confirm button (default: "Delete")
 * @param {string} triggerLabel - Label for trigger button (default: "Delete")
 * @param {boolean} showTrigger - Whether to show the trigger button (default: true)
 */
const DeleteConfirmDialog = ({
  title,
  message,
  onConfirm,
  onCancel,
  showWarning,
  isLoading = false,
  confirmLabel = 'Delete',
  triggerLabel = 'Delete',
  showTrigger = true,
  onTrigger,
}) => {
  const classes = useStyles();

  if (showWarning) {
    return (
      <Paper className={classes.warningPaper} elevation={0}>
        <Typography variant="h6" className={classes.warningTitle}>
          ⚠️ {title}
        </Typography>
        <Typography variant="body2" className={classes.warningText}>
          {message}
        </Typography>
        <Box className={classes.buttonContainer}>
          <Button
            className={classes.deleteButton}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {confirmLabel}
          </Button>
          <Button
            color="default"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    );
  }

  if (showTrigger) {
    return (
      <Button
        onClick={onTrigger}
        className={classes.triggerButton}
        startIcon={<DeleteIcon />}
      >
        {triggerLabel}
      </Button>
    );
  }

  return null;
};

export default DeleteConfirmDialog;